/**
 * Support correction of a partner's rupee wallet (VALUE credits, 1 credit = ₹1): add or remove money after a dispute,
 * chargeback or goodwill decision. Mirrors GrantRevokeCreditsModal, but the amount is rupees and the reason is required.
 */
import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ConfirmModal } from './ConfirmModal';
import { walletAdjustSchema, type WalletAdjustInput } from '../schemas';
import { useGrantWallet, useRevokeWallet } from '../hooks';

interface Props {
  open: boolean;
  mode: 'grant' | 'revoke';
  userId: string;
  /** Current wallet balance in ₹ — shown for context and to stop an over-revoke before it is sent. */
  balance: number;
  onClose: () => void;
}

export function WalletAdjustModal({ open, mode, userId, balance, onClose }: Props) {
  const isGrant = mode === 'grant';
  const grant = useGrantWallet(userId);
  const revoke = useRevokeWallet(userId);
  const mutation = isGrant ? grant : revoke;
  const form = useForm<WalletAdjustInput>({ resolver: zodResolver(walletAdjustSchema), defaultValues: { amount: 100, reason: '' } });
  const grantKey = useRef(crypto.randomUUID()); // reused on retry so a grant can never land twice

  // A new key per OPENING of the modal: reusing one after the admin cancelled and came back with a different correction
  // would make the backend treat it as a repeat of the earlier grant. A retry while the modal stays open reuses it.
  const close = () => {
    grantKey.current = crypto.randomUUID();
    onClose();
  };

  const submit = form.handleSubmit(async (values) => {
    if (!isGrant && values.amount > balance) {
      form.setError('amount', { message: `Wallet holds ₹${balance.toLocaleString('en-IN')} — cannot revoke more.` });
      return;
    }
    try {
      await mutation.mutateAsync({ amount: values.amount, reason: values.reason, ...(isGrant ? { idempotencyKey: grantKey.current } : {}) });
    } catch {
      return; // shown below; keep the modal and the key for a retry
    }
    if (isGrant) grantKey.current = crypto.randomUUID();
    form.reset();
    onClose();
  });

  return (
    <ConfirmModal
      open={open}
      title={isGrant ? 'Add to wallet' : 'Remove from wallet'}
      description={`Current wallet balance: ₹${balance.toLocaleString('en-IN')}. This is recorded in the ledger and the admin audit log with your reason.`}
      confirmLabel={isGrant ? 'Add to wallet' : 'Remove from wallet'}
      destructive={!isGrant}
      loading={mutation.isPending}
      onClose={close}
      onConfirm={() => void submit()}
    >
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant mb-1">Amount (₹)</label>
          <input
            type="number"
            min={1}
            {...form.register('amount', { valueAsNumber: true })}
            className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-electric-violet/30"
          />
          {form.formState.errors.amount && <p className="text-xs text-red-300 mt-1">{form.formState.errors.amount.message}</p>}
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant mb-1">Reason (required)</label>
          <textarea
            {...form.register('reason')}
            rows={2}
            className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-electric-violet/30"
            placeholder="Why is the balance being changed? (ticket / chargeback reference)"
          />
          {form.formState.errors.reason && <p className="text-xs text-red-300 mt-1">{form.formState.errors.reason.message}</p>}
        </div>
        {mutation.error && <p className="text-xs text-red-300">{(mutation.error as Error).message}</p>}
      </form>
    </ConfirmModal>
  );
}
